/**
 * What the face is doing.
 *
 * Five come from `TurnState` — where the turn loop is. `celebrate` comes from
 * a `ReactionCue`, which is the other signal: how the learner is doing.
 */
export const EXPRESSIONS = ['neutral', 'listening', 'thinking', 'speaking', 'concern', 'celebrate'] as const;

export type Expression = (typeof EXPRESSIONS)[number];

/**
 * PRD §8.1's table.
 *
 * Takes the lowercased state because that is what `SessionView` stores; the
 * GraphQL enum's own spelling stops at the subscription boundary.
 */
export function expressionFor(state: string): Expression {
  switch (state) {
    case 'transcribing':
      return 'listening';
    case 'thinking':
      return 'thinking';
    case 'speaking':
      return 'speaking';
    case 'needs_repeat':
      return 'concern';
    // `idle`, and anything this build has not heard of. Falling back to the
    // last expression would be worse than neutral: a face still mouthing
    // words claims the tutor is talking when the server has moved on.
    default:
      return 'neutral';
  }
}

/**
 * What a `ReactionCue` shows on the face, or `null` to leave it alone.
 *
 * Three cues share `celebrate`. At the 38px size adult mode uses, the
 * difference between delighted and impressed is not legible, and three nearly
 * identical poses would be three things to keep in step for one visible
 * result. The distinction survives where it can actually be read — in what the
 * tutor says.
 *
 * `null` for a cue this bundle predates: the face should keep doing whatever
 * it was, not adopt something arbitrary.
 */
export function expressionForCue(cue: string): Expression | null {
  switch (cue) {
    case 'CELEBRATE':
    case 'DELIGHTED':
    case 'IMPRESSED':
      return 'celebrate';
    case 'ENCOURAGING':
    case 'CURIOUS':
      return 'listening';
    default:
      return null;
  }
}
